import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, ShieldAlert, RefreshCw, Send, Undo2, FileText, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { ResendReportButton } from "@/components/admin/ResendReportButton";

const PHOTO_CATS = ["additional", "tank_exterior", "bottom_sediment", "water_clarity", "water_colour",
  "lid_access", "inlet_screen", "overflow_screen", "gutter", "gutter_guard", "leaf_eater",
  "first_flush", "catchment", "filtration", "uv", "under_sink", "lab_sample"];

const NARRATIVE_LABELS = [
  ["inspection_summary", "Inspection Summary"],
  ["tank_storage", "Tank & Storage"],
  ["roof_catchment", "Roof & Catchment"],
  ["filtration_treatment", "Filtration & Treatment"],
];

const REVIEW_CHIP = {
  pending_review: ["Pending review", "bg-amber-100 text-amber-800 border border-amber-300"],
  approved: ["Approved & sent", "bg-emerald-100 text-emerald-800 border border-emerald-300"],
  returned: ["Returned to technician", "bg-slate-200 text-slate-700"],
};

const scoreColor = (n) => (n >= 85 ? "#059669" : n >= 70 ? "#65a30d" : n >= 50 ? "#d97706" : "#ea580c");

function ScoreClock({ score, band, deductions }) {
  const r = 42, circ = 2 * Math.PI * r;
  return (
    <div data-testid="condition-score" className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-4">
      <svg width="104" height="104" viewBox="0 0 104 104" className="shrink-0">
        <circle cx="52" cy="52" r={r} fill="none" stroke="#e2e8f0" strokeWidth="9" />
        <circle cx="52" cy="52" r={r} fill="none" stroke={scoreColor(score)} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${(score / 100) * circ} ${circ}`} transform="rotate(-90 52 52)" />
        <text x="52" y="52" textAnchor="middle" fontSize="26" fontWeight="800" fill="#0f172a">{score}</text>
        <text x="52" y="66" textAnchor="middle" fontSize="9" fill="#64748b">/ 100</text>
      </svg>
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Visible System Condition</div>
        <div className="text-sm font-bold mt-0.5" style={{ color: scoreColor(score) }}>{band}</div>
        <div className="text-[10px] text-slate-400 mt-1 leading-snug">Deterministic score from the recorded findings — visual inspection only, not a measure of water quality. Laboratory analysis is required.</div>
        {deductions?.length > 0 && (
          <details className="mt-1">
            <summary className="text-[11px] font-bold text-cyan-700 cursor-pointer">What affected the score ({deductions.length})</summary>
            <ul className="mt-1 text-[11px] text-slate-600 list-disc pl-4">
              {deductions.map((d, i) => <li key={i}>{d.label} <span className="text-slate-400">(−{d.points})</span></li>)}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

// Authenticated <img> — anchor/img tags can't send the Bearer header.
function AuthImg({ path, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(path, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className={`${className} bg-slate-100`} />;
  return <img src={src} alt={alt} className={className} />;
}

// Admin AI-report review: findings + narrative + photos + PDF preview,
// with Approve & Send / Regenerate / Return for review.
export default function AdminInspectionReview() {
  const { cid, iid } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [photoCategory, setPhotoCategory] = useState("additional");
  const photoInputRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/admin/property-campaigns/${cid}/inspections/${iid}/review`)
      .then(({ data: d }) => {
        setData(d);
        setDraft({ narratives: { ...d.narratives }, items: (d.items_worth_reviewing || []).join("\n") });
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load review"));
  }, [cid, iid]);
  useEffect(load, [load]);

  const act = async (action, fn) => {
    if (busy) return;
    setBusy(action);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); }
    finally { setBusy(""); }
  };

  const approve = () => act("approve", async () => {
    const { data: r } = await api.post(`/admin/property-campaigns/${cid}/inspections/${iid}/review/approve`);
    toast.success(r.duplicate ? "Already approved" : `Report sent — email ${r.email_status || "queued"}`);
    load();
  });
  const RETRY_RESULT_LABEL = {
    attached: "Attached", already_attached: "Already attached",
    failed: "Failed", not_eligible: "Not eligible",
  };
  const retryAttach = () => act("retry-attach", async () => {
    const { data: r } = await api.post(`/admin/integrations/servicem8/inspections/${iid}/attach-report`);
    const label = RETRY_RESULT_LABEL[r.result] || r.result;
    if (r.result === "attached" || r.result === "already_attached") toast.success(label);
    else toast.error(`${label}${r.reason || r.error ? ` — ${r.reason || r.error}` : ""}`);
    load();
  });
  const regenerate = () => act("regenerate", async () => {
    const { data: r } = await api.post(`/admin/property-campaigns/${cid}/inspections/${iid}/review/regenerate`);
    toast.success(`Narrative regenerated (${r.ai_report_status})`);
    setEditing(false);
    load();
  });
  const returnInspection = () => act("return", async () => {
    if (!window.confirm("Return this inspection to the technician for review? The appointment reopens.")) return;
    await api.post(`/admin/property-campaigns/${cid}/inspections/${iid}/review/return`);
    toast.success("Inspection returned to technician");
    load();
  });
  const saveEdit = () => act("edit", async () => {
    const items = draft.items.split("\n").map((x) => x.trim()).filter(Boolean);
    const { data: r } = await api.post(`/admin/property-campaigns/${cid}/inspections/${iid}/review/edit`,
      { narratives: draft.narratives, items_worth_reviewing: items });
    toast.success(r.safety_flags?.length ? "Saved — but safety flags remain" : "Narrative updated");
    setEditing(false);
    load();
  });

  const openPdf = () => act("pdf", async () => {
    const { data: blob } = await api.get(`/field/inspection/${iid}/report.pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([blob], { type: "application/pdf" })), "_blank");
  });

  const addPhoto = (e) => act("photo", async () => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", photoCategory);
    const { data: added } = await api.post(`/admin/property-campaigns/${cid}/inspections/${iid}/photos`, fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (added?.storage_path) {
      toast.success("Photo added — regenerate the report to include it in the PDF");
    } else {
      toast.warning("Photo added, but cloud storage was unreachable — it may not survive the next deploy. Contact support if this persists.");
    }
    load();
  });

  const removePhoto = (pid) => act("photo", async () => {
    if (!window.confirm("Remove this photo from the inspection?")) return;
    await api.delete(`/admin/property-campaigns/${cid}/inspections/${iid}/photos/${pid}`);
    toast.success("Photo removed — regenerate the report to update the PDF");
    load();
  });

  if (!data) return <PortalShell><div className="p-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div></PortalShell>;
  const { inspection: insp, photos, offer, narratives, narrative_source, condition_score: cs } = data;
  const ai = insp.ai_report || {};
  const flags = ai.safety_flags || [];
  const approved = insp.review_status === "approved";
  const [chipLabel, chipCls] = REVIEW_CHIP[insp.review_status] || ["—", "bg-slate-100 text-slate-500"];
  const s = insp.sections || {};

  const StatusRow = ({ label, value }) => (
    <div className="flex justify-between text-xs py-1 border-b border-slate-100">
      <span className="text-slate-500">{label}</span>
      <span className={`font-bold ${["Good", "Clear", "Colourless", "None visible", "Secure", "Collected"].some((g) => String(value || "").startsWith(g)) ? "text-emerald-700" : ["Attention Suggested", "Heavy", "Moderate debris", "Heavy debris"].includes(value) ? "text-amber-700" : "text-slate-600"}`}>{value || "—"}</span>
    </div>
  );

  return (
    <PortalShell>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="inspection-review">
        <Link to={`/admin/property-campaigns/${cid}`} className="text-sm text-cyan-700 font-semibold inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to campaign</Link>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">Inspection Report Review</h1>
          <span data-testid="review-status-chip" className={`px-3 py-1 rounded-full text-xs font-bold ${chipCls}`}>{chipLabel}</span>
          {ai.status === "generating" && <span data-testid="ai-generating-chip" className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-100 text-cyan-800 border border-cyan-300 inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI narrative generating…</span>}
          {ai.status === "failed" && <span data-testid="ai-failed-chip" className="px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-300">AI generation failed</span>}
          {flags.length > 0 && <span data-testid="safety-flag-chip" className="px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-300 inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> {flags.length} safety flag{flags.length > 1 ? "s" : ""}</span>}
          {ai.edited && <span className="px-3 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-800 border border-violet-300">Manually edited</span>}
        </div>
        <div className="text-sm text-slate-500 mt-1">{insp.street_address}, {insp.suburb} · {insp.first_name} · Technician: {insp.technician}</div>
        <div className="text-xs text-slate-400 mt-0.5">
          {ai.model && <>Model {ai.model} · prompt {ai.prompt_version} · attempt {ai.attempt}{ai.input_hash ? ` · input ${ai.input_hash.slice(0, 10)}…` : ""}</>}
          {narrative_source === "structured_fallback" && " · Narrative: structured fallback (no AI text yet)"}
        </div>

        {flags.length > 0 && (
          <div data-testid="safety-flags-panel" className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm">
            <div className="font-bold text-rose-800">Prohibited phrases detected — review before sending</div>
            <ul className="mt-1 text-xs text-rose-700 list-disc pl-5">
              {flags.map((f, i) => <li key={i}><b>{f.section}</b>: "{f.phrase}"</li>)}
            </ul>
          </div>
        )}
        {ai.status === "failed" && (
          <div data-testid="ai-failed-panel" className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            Narrative generation failed ({ai.error || "unknown error"}). The structured findings are safe. Retry generation, or edit the fallback narrative below and approve manually.
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-1 space-y-4">
            {cs && <ScoreClock score={cs.score} band={cs.band} deductions={cs.deductions} />}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Structured findings</h3>
              <StatusRow label="Supplies the house" value={(s.tank_config || {}).supplies_house} />
              <StatusRow label="Sediment level" value={(s.tank_water || {}).sediment_level} />
              <StatusRow label="Water clarity" value={(s.tank_water || {}).clarity} />
              <StatusRow label="Water colour" value={(s.tank_water || {}).colour} />
              <StatusRow label="Odour" value={(s.tank_water || {}).odour} />
              <StatusRow label="Inlet screen" value={(s.tank_condition || {}).inlet_screen} />
              {((s.tank_config || {}).tanks || []).map((t, i) => (
                <StatusRow key={t.key || i} label={t.label || `Tank ${i + 1}`}
                  value={[t.size_note, t.note].filter(Boolean).join(" · ") || "Recorded"} />
              ))}
              {(s.catchment || {}).condition
                ? <StatusRow label="Catchment condition" value={(s.catchment || {}).condition} />
                : <StatusRow label="Gutter condition" value={(s.catchment || {}).gutter_condition} />}
              <StatusRow label="Lead flashing" value={(s.catchment || {}).lead_flashing} />
              {(s.catchment || {}).overall && <StatusRow label="Catchment overall" value={(s.catchment || {}).overall} />}
              <StatusRow label="Filtration installed" value={(s.filtration || {}).installed} />
              {(s.filtration || {}).recommend_upgrade === true &&
                <StatusRow label="Filtration upgrade" value="Recommended by technician" />}
              <StatusRow label="UV installed" value={(s.uv || {}).installed} />
              {(s.uv || {}).installed === "No" &&
                <StatusRow label="UV upgrade" value="Auto-suggested (no UV installed)" />}
              {(s.under_sink || {}).present && <StatusRow label="Under-sink filtration" value={(s.under_sink || {}).present} />}
              <StatusRow label="Testing history" value={(s.testing_history || {}).value} />
              <StatusRow label="Lab sample" value={(s.sample || {}).requested === "Yes" ? `${(s.sample || {}).collected === "Yes" ? "Collected" : "Requested"} — ${(s.sample || {}).source || ""}` : "Not requested"} />
              {(insp.customer_concerns || []).length > 0 && (
                <div className="mt-3 text-xs"><span className="text-slate-500">Customer concerns:</span> <span className="font-semibold text-slate-800">{insp.customer_concerns.join(" · ")}</span></div>
              )}
              {(s.tech_note || {}).text && <div className="mt-2 text-xs text-slate-600">Technician note: "{s.tech_note.text}"</div>}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Photos ({photos.length})</h3>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative group">
                    <AuthImg path={`/field/inspection/${insp.id}/photos/${p.id}`} alt={p.category} className="rounded-lg object-cover w-full h-20 border border-slate-200" />
                    <button type="button" title="Remove photo" data-testid={`review-photo-remove-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1 right-1 rounded-full bg-white/90 border border-slate-200 text-rose-600 w-5 h-5 text-xs font-bold leading-none opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                    <div className="text-[10px] text-slate-500 mt-0.5">{p.category.replace(/_/g, " ")}{p.caption ? ` — ${p.caption}` : ""}</div>
                  </div>
                ))}
                {photos.length === 0 && <div className="text-xs text-slate-400 col-span-2">No photos uploaded</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Add / repair photo</div>
                <select value={photoCategory} onChange={(e) => setPhotoCategory(e.target.value)} data-testid="review-photo-category"
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs mb-1.5 bg-white">
                  {PHOTO_CATS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </select>
                <input ref={photoInputRef} type="file" accept="image/*" data-testid="review-photo-file"
                  onChange={addPhoto} disabled={busy === "photo"}
                  className="block w-full text-xs text-slate-500 file:mr-2 file:rounded-full file:border-0 file:bg-cyan-700 file:text-white file:px-3 file:py-1.5 file:text-xs file:font-bold" />
                <div className="text-[10px] text-slate-400 mt-1.5">Photos can be added even after completion — click "Regenerate Report" afterwards to rebuild the PDF.</div>
              </div>
            </div>
            {offer && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs" data-testid="review-offer">
                <div className="font-bold text-emerald-800">20% offer active</div>
                <div className="text-emerald-700 mt-1">Code {offer.discount_code} · expires {new Date(offer.expires_at).toLocaleDateString("en-AU")}</div>
              </div>
            )}
          </div>

          <div className="md:col-span-2 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">AI-generated narrative</h3>
                {!approved && !editing && (
                  <button data-testid="edit-narrative-btn" onClick={() => setEditing(true)} className="text-xs font-bold text-cyan-700 inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                )}
              </div>
              {NARRATIVE_LABELS.map(([key, label]) => (
                <div key={key} className="mt-4">
                  <div className="text-sm font-bold text-slate-900">{label}</div>
                  {editing ? (
                    <textarea data-testid={`narrative-edit-${key}`} value={draft.narratives[key] || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, narratives: { ...d.narratives, [key]: e.target.value } }))}
                      className="mt-1 w-full rounded-xl border border-slate-300 p-2 text-sm" rows={key === "inspection_summary" ? 5 : 3} />
                  ) : (
                    <p data-testid={`narrative-${key}`} className="mt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{narratives[key] || "—"}</p>
                  )}
                </div>
              ))}
              <div className="mt-4">
                <div className="text-sm font-bold text-slate-900">Items Worth Reviewing</div>
                {editing ? (
                  <textarea data-testid="narrative-edit-items" value={draft.items}
                    onChange={(e) => setDraft((d) => ({ ...d, items: e.target.value }))}
                    placeholder="One item per line" className="mt-1 w-full rounded-xl border border-slate-300 p-2 text-sm" rows={5} />
                ) : (
                  <ul data-testid="items-worth-reviewing" className="mt-1 text-sm text-slate-600 list-disc pl-5">
                    {(data.items_worth_reviewing || []).map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                )}
              </div>
              {editing && (
                <div className="mt-4 flex gap-2">
                  <button data-testid="save-edit-btn" onClick={saveEdit} disabled={busy === "edit"} className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-bold disabled:opacity-50">{busy === "edit" ? "Saving…" : "Save edits"}</button>
                  <button onClick={() => { setEditing(false); setDraft({ narratives: { ...data.narratives }, items: (data.items_worth_reviewing || []).join("\n") }); }} className="px-4 py-2 rounded-full border border-slate-300 text-slate-600 text-sm font-bold">Cancel</button>
                </div>
              )}
              <p className="mt-4 text-[11px] text-slate-400">The "Recommended Next Step: Laboratory Water Analysis" section and the 20% offer block are added automatically to every report and cannot be removed.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Actions</h3>
              <div className="flex flex-wrap gap-2">
                <button data-testid="pdf-preview-btn" onClick={openPdf} disabled={busy === "pdf"}
                  className="px-4 py-2.5 rounded-full border-2 border-slate-300 text-slate-700 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"><FileText className="w-4 h-4" /> {busy === "pdf" ? "Opening…" : "PDF preview"}</button>
                {!approved && (
                  <>
                    <button data-testid="regenerate-btn" onClick={regenerate} disabled={!!busy}
                      className="px-4 py-2.5 rounded-full border-2 border-cyan-600 text-cyan-700 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                      <RefreshCw className={`w-4 h-4 ${busy === "regenerate" ? "animate-spin" : ""}`} /> {busy === "regenerate" ? "Regenerating…" : "Regenerate Report"}</button>
                    <button data-testid="return-btn" onClick={returnInspection} disabled={!!busy}
                      className="px-4 py-2.5 rounded-full border-2 border-slate-300 text-slate-600 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"><Undo2 className="w-4 h-4" /> Return for Review</button>
                    <button data-testid="approve-send-btn" onClick={approve} disabled={!!busy}
                      className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                      <Send className="w-4 h-4" /> {busy === "approve" ? "Sending…" : "Approve & Send"}</button>
                  </>
                )}
                {approved && (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-sm text-emerald-700 font-semibold" data-testid="approved-note">
                      Approved {insp.approved_at ? new Date(insp.approved_at).toLocaleString("en-AU") : ""} by {insp.approved_by} — report emailed with the 20% offer.
                    </div>
                    <div className="px-3 py-1.5 rounded-full border-2 border-cyan-600">
                      <ResendReportButton
                        campaignId={cid}
                        inspection={insp}
                        onSent={load}
                        label="Resend report"
                      />
                    </div>
                    {insp.sm8_attachment_status === "attached" ? (
                      <div className="text-xs text-slate-500" data-testid="sm8-attachment-attached-note">
                        ServiceM8 attachment: attached
                      </div>
                    ) : (
                      <button data-testid="retry-sm8-attach-btn" onClick={retryAttach} disabled={!!busy}
                        className="px-4 py-2.5 rounded-full border-2 border-amber-500 text-amber-700 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                        <RefreshCw className={`w-4 h-4 ${busy === "retry-attach" ? "animate-spin" : ""}`} />
                        {busy === "retry-attach" ? "Retrying…" : "Retry ServiceM8 Attachment"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {!approved && flags.length > 0 && <p className="mt-2 text-xs text-rose-600">Safety flags present — regenerate or edit the narrative before approving.</p>}
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
